/**
 * @file index.tsx
 *
 * @fileoverview Renders the google gtm tag in the page.
 */

import React, { useContext, useEffect, useRef } from 'react';
import Script from 'next/script';
import { UserContext, UserContextInterface } from '~/jobiqo/context/user';
import { checkUserConsentForService } from '~/jobiqo/services/cmp';
import Router from 'next/router';
import { SERVICES } from '~/jobiqo/lib/cmp';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const usePageReloadIfGTMConsentGotRemoved = (currentGTMConsent) => {
  // If the consent has changed from true to false we need to reload
  // the page otherwise GTM does not get excluded.
  const previousGTMConsent = useRef<boolean>(null);
  const userContext: UserContextInterface = useContext(UserContext);

  // Save current consent state to compare.
  useEffect(() => {
    previousGTMConsent.current = checkUserConsentForService(
      userContext?.consents,
      SERVICES.GoogleTagmanager,
    );
  });

  // Compare if consent has changed from true to false.
  if (previousGTMConsent.current && !currentGTMConsent) {
    Router.reload();
  }
};

export const GTM = () => {
  const testing = process.env.testing;
  const staging = process.env.staging;
  const userContext: UserContextInterface = useContext(UserContext);
  const { APP } = useFrontendConfig();
  const GTM_CONTAINER_ID = APP.GTM_CONTAINER_ID;
  // By default, GTM script url is https://www.googletagmanager.com/gtm.js, but
  // we want to be able to override it, so we can use GTM script hosted by
  // clients on their own.
  const GTM_SCRIPT_URL = APP.GTM_SCRIPT_URL;
  // Configurable script loading strategy. Defaults to 'lazyOnload'.
  // Can be overridden per client via GTM_SCRIPT_STRATEGY in app config.
  const scriptStrategy = APP.GTM_SCRIPT_STRATEGY;

  const isUserConsentNeededAndGiven = checkUserConsentForService(
    userContext?.consents,
    SERVICES.GoogleTagmanager,
  );

  usePageReloadIfGTMConsentGotRemoved(isUserConsentNeededAndGiven);
  return isUserConsentNeededAndGiven &&
    GTM_CONTAINER_ID &&
    !testing &&
    !staging ? (
    <>
      <Script id="gtm-config" strategy={scriptStrategy}>
        {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          '${GTM_SCRIPT_URL}?id='+i+dl;f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer', '${GTM_CONTAINER_ID}');`}
      </Script>
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`}
          height="0"
          width="0"
          style={{
            display: 'none',
            visibility: 'hidden',
          }}
        />
      </noscript>
    </>
  ) : null;
};
